import React, {
  createContext,
  CSSProperties,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

// ============================================================================
// Constants
// ============================================================================

const FADE_IN_MS = 300;
const FADE_OUT_MS = 100;
const MOUNT_DELAY_MS = 10;
const HOVER_GRACE_PERIOD_MS = 100;
const TOOLTIP_OFFSET_PX = 8;
const VIEWPORT_MARGIN_PX = 16;

const BASE_STYLES: CSSProperties = {
  position: 'absolute',
  background: '#000',
  color: '#fff',
  padding: '5px 10px',
  borderRadius: '5px',
  fontSize: '15px',
  zIndex: 9999,
};

const TRANSFORM_BY_PLACEMENT: Record<Placement, string> = {
  top: 'translate(-50%, -100%)',
  right: 'translate(0, -50%)',
  bottom: 'translate(-50%, 0)',
  left: 'translate(-100%, -50%)',
};

// ============================================================================
// Types
// ============================================================================

type Placement = 'top' | 'right' | 'bottom' | 'left';

interface Position {
  top: number;
  left: number;
}

interface TooltipPortalProps {
  targetId: string;
  content: ReactNode;
  placement?: Placement;
  styleOverrides?: CSSProperties;
  locationId?: string;
  limitWidth?: number;
  persistent?: boolean;
}

// ============================================================================
// Tooltip Manager Context (replaces global mutable state)
// ============================================================================

interface TooltipManagerContextValue {
  requestShow: (id: string, hide: () => void) => void;
  notifyHidden: (id: string) => void;
}

const TooltipManagerContext = createContext<TooltipManagerContextValue | null>(null);

export const TooltipManagerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const activeRef = useRef<{ id: string; hide: () => void } | null>(null);

  const requestShow = useCallback((id: string, hide: () => void) => {
    if (activeRef.current && activeRef.current.id !== id) {
      activeRef.current.hide();
    }
    activeRef.current = { id, hide };
  }, []);

  const notifyHidden = useCallback((id: string) => {
    if (activeRef.current?.id === id) {
      activeRef.current = null;
    }
  }, []);

  return (
    <TooltipManagerContext.Provider value={{ requestShow, notifyHidden }}>{children}</TooltipManagerContext.Provider>
  );
};

const useTooltipManager = () => useContext(TooltipManagerContext);

// ============================================================================
// Position calculation hook
// ============================================================================

const useTooltipPosition = (targetId: string, locationId: string | undefined, placement: Placement) => {
  const calculatePosition = useCallback((): Position | null => {
    const target = document.getElementById(targetId);
    const anchor = locationId ? document.getElementById(locationId) : target;
    if (!anchor) return null;

    const rect = anchor.getBoundingClientRect();
    const scrollY = window.scrollY || document.documentElement.scrollTop;
    const scrollX = window.scrollX || document.documentElement.scrollLeft;

    const positions: Record<Placement, Position> = {
      top: {
        top: rect.top + scrollY - TOOLTIP_OFFSET_PX,
        left: rect.left + scrollX + rect.width / 2,
      },
      right: {
        top: rect.top + scrollY + rect.height / 2,
        left: rect.left + scrollX + rect.width + TOOLTIP_OFFSET_PX,
      },
      bottom: {
        top: rect.top + scrollY + rect.height + TOOLTIP_OFFSET_PX,
        left: rect.left + scrollX + rect.width / 2,
      },
      left: {
        top: rect.top + scrollY + rect.height / 2,
        left: rect.left + scrollX - TOOLTIP_OFFSET_PX,
      },
    };

    return positions[placement];
  }, [targetId, locationId, placement]);

  return calculatePosition;
};

// ============================================================================
// Visibility state hook
// ============================================================================

type VisibilityState = 'hidden' | 'mounting' | 'visible' | 'unmounting';

const useTooltipVisibility = (tooltipId: string) => {
  const [state, setState] = useState<VisibilityState>('hidden');
  const manager = useTooltipManager();
  const unmountTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const mountTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const show = useCallback(() => {
    clearTimeout(unmountTimeoutRef.current);
    clearTimeout(mountTimeoutRef.current);

    manager?.requestShow(tooltipId, () => hide());

    setState('mounting');
    mountTimeoutRef.current = setTimeout(() => setState('visible'), MOUNT_DELAY_MS);
  }, [tooltipId, manager]);

  const hide = useCallback(() => {
    clearTimeout(mountTimeoutRef.current);

    setState('unmounting');
    manager?.notifyHidden(tooltipId);

    unmountTimeoutRef.current = setTimeout(() => setState('hidden'), FADE_OUT_MS);
  }, [tooltipId, manager]);

  useEffect(() => {
    return () => {
      clearTimeout(unmountTimeoutRef.current);
      clearTimeout(mountTimeoutRef.current);
      manager?.notifyHidden(tooltipId);
    };
  }, [tooltipId, manager]);

  const isMounted = state !== 'hidden';
  const isVisible = state === 'visible';

  return { isMounted, isVisible, show, hide };
};

// ============================================================================
// Target event listener hook
// ============================================================================

const useTargetEvents = (
  targetId: string,
  persistent: boolean,
  onEnter: () => void,
  onLeave: () => void,
  setIsOverTarget: (value: boolean) => void,
  suppressReopenRef: React.MutableRefObject<boolean>,
) => {
  useEffect(() => {
    const target = document.getElementById(targetId);
    if (!target) return;

    const handleEnter = () => {
      setIsOverTarget(true);
      // Escape-to-close (see usePersistentKeyboardBridge) restores focus to the target as part of
      // closing it - without this flag, that focus() call would immediately re-trigger this same
      // handler and reopen the tooltip it was just asked to close.
      if (suppressReopenRef.current) {
        suppressReopenRef.current = false;
        return;
      }
      onEnter();
    };

    const handleLeave = () => {
      setIsOverTarget(false);
      if (!persistent) {
        onLeave();
      }
    };

    target.addEventListener('mouseenter', handleEnter);
    target.addEventListener('mouseleave', handleLeave);
    // A keyboard user tabbing to the target has no pointer to hover with - without this, a
    // persistent tooltip's interactive content (e.g. the mac_id link) is only ever reachable by
    // mouse (CodeRabbit finding, MR !1428). Scoped to persistent tooltips only (Magnus Persson
    // review, MR !1428) - a plain hover tooltip has no interactive content to reach by keyboard,
    // and registering this unconditionally made every tooltip in the app open on focus, an
    // undeclared app-wide behaviour change with no test coverage of its own.
    if (persistent) {
      target.addEventListener('focus', handleEnter);
      target.addEventListener('blur', handleLeave);
    }

    return () => {
      target.removeEventListener('mouseenter', handleEnter);
      target.removeEventListener('mouseleave', handleLeave);
      if (persistent) {
        target.removeEventListener('focus', handleEnter);
        target.removeEventListener('blur', handleLeave);
      }
    };
  }, [targetId, persistent, onEnter, onLeave, setIsOverTarget, suppressReopenRef]);
};

// ============================================================================
// Persistent mode keyboard bridge
// ============================================================================

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

// createPortal appends the tooltip's content to the end of document.body, so its place in the
// real DOM tab order has nothing to do with where the target sits on the page - a plain Tab
// press from the target usually lands on some unrelated control instead of this tooltip's own
// interactive content (e.g. the TP1 link), and by the time the user tabs on far enough to find
// it, usePersistentHover's grace-period timeout has already unmounted it. Redirect Tab/Shift+Tab
// explicitly between the two instead of trusting DOM order (CodeRabbit finding, MR !1428).
const usePersistentKeyboardBridge = (
  targetId: string,
  nodeRef: React.RefObject<HTMLDivElement>,
  persistent: boolean,
  isMounted: boolean,
  hide: () => void,
  suppressReopenRef: React.MutableRefObject<boolean>,
) => {
  useEffect(() => {
    if (!persistent) return;
    const target = document.getElementById(targetId);
    if (!target) return;

    const handleTargetKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        hide();
        return;
      }
      if (event.key !== 'Tab' || event.shiftKey || !isMounted) return;
      const focusable = nodeRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      if (!focusable) return;
      event.preventDefault();
      focusable.focus();
    };

    const handleContentKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        hide();
        // Restoring focus to the target would otherwise re-trigger its own focus listener and
        // reopen the tooltip this Escape press just asked to close - see useTargetEvents.
        suppressReopenRef.current = true;
        target.focus();
        return;
      }
      if (event.key === 'Tab' && event.shiftKey) {
        event.preventDefault();
        target.focus();
      }
    };

    const node = nodeRef.current;
    target.addEventListener('keydown', handleTargetKeyDown);
    node?.addEventListener('keydown', handleContentKeyDown);

    return () => {
      target.removeEventListener('keydown', handleTargetKeyDown);
      node?.removeEventListener('keydown', handleContentKeyDown);
    };
  }, [targetId, nodeRef, persistent, isMounted, hide, suppressReopenRef]);
};

// Exposes the target/tooltip relationship to assistive tech the same way it's exposed to a
// keyboard user by usePersistentKeyboardBridge above - aria-expanded reflects whether the
// content is currently reachable at all, and aria-describedby points a screen reader at it.
const usePersistentAriaState = (
  targetId: string,
  tooltipId: string,
  persistent: boolean,
  isMounted: boolean,
) => {
  useEffect(() => {
    if (!persistent) return;
    const target = document.getElementById(targetId);
    if (!target) return;

    if (isMounted) {
      target.setAttribute('aria-expanded', 'true');
      target.setAttribute('aria-describedby', tooltipId);
    } else {
      target.setAttribute('aria-expanded', 'false');
      target.removeAttribute('aria-describedby');
    }

    return () => {
      target.removeAttribute('aria-expanded');
      target.removeAttribute('aria-describedby');
    };
  }, [targetId, tooltipId, persistent, isMounted]);
};

// ============================================================================
// Persistent mode hover hook
// ============================================================================

const usePersistentHover = (
  persistent: boolean,
  isMounted: boolean,
  isOverTarget: boolean,
  isOverTooltip: boolean,
  hide: () => void,
) => {
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!persistent) return;

    if (!isOverTarget && !isOverTooltip && isMounted) {
      hideTimeoutRef.current = setTimeout(hide, HOVER_GRACE_PERIOD_MS);
    } else {
      clearTimeout(hideTimeoutRef.current);
    }

    return () => clearTimeout(hideTimeoutRef.current);
  }, [persistent, isMounted, isOverTarget, isOverTooltip, hide]);
};

// ============================================================================
// Viewport-clamp hook
// ============================================================================

// The position calculated above only ever centers the tooltip on its target - it has no idea
// where the viewport edges are, so a long nowrap label on a target near the edge of the screen
// (e.g. the last action icon in a table's rightmost column) renders straight past the browser
// boundary. This measures the tooltip after it mounts and nudges it back on-screen, keeping the
// arrow (a separate child element, centered on the box by its own CSS) visually pointing at the
// same spot on the target rather than drifting along with the correction.
const useViewportClamp = (
  nodeRef: React.RefObject<HTMLDivElement>,
  arrowRef: React.RefObject<HTMLSpanElement>,
  placement: Placement,
  isMounted: boolean,
  position: Position | null,
  content: ReactNode,
) => {
  // Tracks whether the width-cap branch below set these three properties itself on the last run,
  // so the reset can undo exactly that without ever touching a maxWidth/whiteSpace the caller (or
  // buildStyles' own default nowrap) set through React's normal `style` prop rendering.
  const appliedWidthCapRef = useRef(false);

  useLayoutEffect(() => {
    const node = nodeRef.current;
    const arrow = arrowRef.current;
    if (!isMounted || !position || !node) return;

    // Reset any correction left over from a previous show before re-measuring - otherwise a
    // stale shift would throw off this measurement.
    node.style.transform = TRANSFORM_BY_PLACEMENT[placement];
    if (arrow) arrow.style.transform = '';
    if (appliedWidthCapRef.current) {
      node.style.maxWidth = '';
      node.style.whiteSpace = '';
      node.style.wordWrap = '';
      appliedWidthCapRef.current = false;
    }

    // clientWidth excludes the vertical scrollbar's own width - window.innerWidth does not, so
    // clamping against innerWidth let the tooltip's edge land under the scrollbar track, which
    // was itself enough overflow to trigger a *new* horizontal scrollbar on the page.
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const maxAvailableWidth = viewportWidth - VIEWPORT_MARGIN_PX * 2;

    let rect = node.getBoundingClientRect();
    // A box wider than the viewport itself can overflow both edges at once - no horizontal shift
    // below can fix both sides together, since correcting one edge only pushes the other further
    // off-screen. Cap the box to what actually fits and let it wrap, then re-measure before
    // computing any shift (CodeRabbit finding, MR !1428).
    if (rect.width > maxAvailableWidth && maxAvailableWidth > 0) {
      node.style.maxWidth = `${maxAvailableWidth}px`;
      node.style.whiteSpace = 'normal';
      node.style.wordWrap = 'break-word';
      appliedWidthCapRef.current = true;
      rect = node.getBoundingClientRect();
    }

    const overflowRight = rect.right - (viewportWidth - VIEWPORT_MARGIN_PX);
    const overflowLeft = VIEWPORT_MARGIN_PX - rect.left;

    let shift = 0;
    if (overflowRight > 0) shift = -overflowRight;
    else if (overflowLeft > 0) shift = overflowLeft;

    if (shift === 0) return;

    node.style.transform = `${TRANSFORM_BY_PLACEMENT[placement]} translateX(${shift}px)`;
    if (!arrow) return;

    // top/bottom arrows are centered on the box via `translateX(-50%)` in CSS - fold the
    // counter-shift into that same transform. left/right arrows only ever translateY, so add
    // the counter-shift as a new translateX component.
    arrow.style.transform =
      placement === 'top' || placement === 'bottom'
        ? `translateX(calc(-50% - ${shift}px))`
        : `translateY(-50%) translateX(${-shift}px)`;
  }, [nodeRef, arrowRef, placement, isMounted, position, content]);
};

// ============================================================================
// Style builders
// ============================================================================

// Exported for direct unit testing - jsdom's CSS parser rejects `width: max-content` outright
// (silently drops it, unlike a real browser), so the width fix below can only be verified against
// the plain style object this returns, not against a DOM node's live `style` property.
export const buildStyles = (
  position: Position,
  placement: Placement,
  isVisible: boolean,
  limitWidth: number | undefined,
  persistent: boolean,
  styleOverrides: CSSProperties,
): CSSProperties => {
  // `left` is set below with no `right`/`width`, so an auto width resolves via shrink-to-fit
  // against the space remaining to the right of `left` - for a target in the rightmost column
  // that space can be a fraction of limitWidth, squeezing the box far narrower than intended
  // (Magnus Persson review, MR !1428). `width: max-content` sizes to the content's own preferred
  // width instead, still capped by maxWidth below, so the shift this hook applies afterward can
  // no longer shrink it in the first place.
  const widthStyles: CSSProperties = limitWidth
    ? { width: 'max-content', maxWidth: `${limitWidth}px`, whiteSpace: 'normal', wordWrap: 'break-word' }
    : { whiteSpace: 'nowrap' };

  return {
    ...BASE_STYLES,
    ...widthStyles,
    pointerEvents: persistent ? 'auto' : 'none',
    top: position.top,
    left: position.left,
    transform: TRANSFORM_BY_PLACEMENT[placement],
    opacity: isVisible ? 1 : 0,
    transition: `opacity ${isVisible ? FADE_IN_MS : FADE_OUT_MS}ms ease-in-out`,
    ...styleOverrides,
  };
};

// ============================================================================
// Main Component
// ============================================================================

const TooltipPortal: React.FC<TooltipPortalProps> = ({
  targetId,
  content,
  placement = 'top',
  locationId,
  styleOverrides = {},
  limitWidth,
  persistent = false,
}) => {
  const tooltipId = useId();
  const [position, setPosition] = useState<Position | null>(null);
  const [isOverTarget, setIsOverTarget] = useState(false);
  const [isOverTooltip, setIsOverTooltip] = useState(false);
  const nodeRef = useRef<HTMLDivElement>(null);
  const arrowRef = useRef<HTMLSpanElement>(null);
  const suppressReopenRef = useRef(false);

  const calculatePosition = useTooltipPosition(targetId, locationId, placement);
  const { isMounted, isVisible, show, hide } = useTooltipVisibility(tooltipId);

  const handleShow = useCallback(() => {
    const pos = calculatePosition();
    if (pos) {
      setPosition(pos);
      show();
    }
  }, [calculatePosition, show]);

  useTargetEvents(targetId, persistent, handleShow, hide, setIsOverTarget, suppressReopenRef);
  usePersistentHover(persistent, isMounted, isOverTarget, isOverTooltip, hide);
  usePersistentKeyboardBridge(targetId, nodeRef, persistent, isMounted, hide, suppressReopenRef);
  usePersistentAriaState(targetId, tooltipId, persistent, isMounted);
  useViewportClamp(nodeRef, arrowRef, placement, isMounted, position, content);

  if (!isMounted || !position) return null;

  return createPortal(
    <div
      ref={nodeRef}
      id={persistent ? tooltipId : undefined}
      data-testid="tooltip-portal-box"
      onMouseEnter={() => persistent && setIsOverTooltip(true)}
      onMouseLeave={() => persistent && setIsOverTooltip(false)}
      // React's onFocus/onBlur behave like focusin/focusout (they bubble), so this fires for
      // focus moving onto any interactive content inside (e.g. the mac_id tooltip's link) too -
      // without it, tabbing off the target into the tooltip's own content would still hide it
      // after the grace period, since nothing else marks focus as "still within the tooltip".
      onFocus={() => persistent && setIsOverTooltip(true)}
      onBlur={() => persistent && setIsOverTooltip(false)}
      style={buildStyles(position, placement, isVisible, limitWidth, persistent, styleOverrides)}
    >
      <span ref={arrowRef} data-testid="tooltip-portal-arrow" className={`tooltip-portal ${placement}`} />
      {content}
    </div>,
    document.body,
  );
};

export default TooltipPortal;
